import { useCallback, useEffect, useRef, useState } from "react";
import { executionReducer, EXECUTION_ACTIONS } from "../domain/executionReducer";
import {
  executionStatus,
  reconcileExecutionRecord,
} from "../domain/executionReconciliation";
import { finalizeIfDue } from "../domain/finalization";
import { applyRestAnchor } from "../domain/restTimer";
import { syncConflictMessage } from "../domain/syncIssue";
import {
  historyEntryFromResponse,
  historyQueryForItem,
  needsOwnHistory,
  withItemHistory,
} from "../domain/previousPerformance";
import { getWorkoutLogHistory } from "../api/workoutLogApi";
import {
  createSubstitutionAction,
  SUBSTITUTION_ERRORS,
  SubstitutionError,
} from "../domain/substitution";
import {
  ExecutionWriteCoordinator,
  workoutLogStore,
} from "../store/localExecutionStore";
import { workoutSyncEngine } from "../sync/syncEngine";
import { executionOpenElsewhereError } from "./useExecutionOwnership";

const OPEN_STATUSES = new Set(["ACTIVE", "FINISHED"]);

function finalizationAction(record, now = new Date()) {
  const decision = finalizeIfDue(record, now);
  if (!decision.due) return null;
  return decision.action === "SAVE"
    ? {
        type: EXECUTION_ACTIONS.SAVE,
        at: decision.savedAt,
        finalizedBy: decision.finalizedBy,
      }
    : { type: EXECUTION_ACTIONS.DISCARD, at: decision.deadline, auto: true };
}

function notEditableError() {
  return new Error("This workout is no longer editable.");
}

function quarantinedExecutionError(code) {
  const error = new Error(syncConflictMessage(code));
  error.code = code;
  error.quarantined = true;
  return error;
}

// A confirmed quarantine deletes the main record, so after a remount or cold
// start the durable quarantine entry is the only truth left to explain why
// this execution is gone.
async function confirmedQuarantineFor(store, userId, executionId) {
  if (typeof store.listQuarantine !== "function") return null;
  const entries = await store.listQuarantine(userId);
  return (entries || [])
    .filter((entry) => entry.executionId === executionId && entry.confirmed === true)
    .sort((left, right) => String(right.createdAt).localeCompare(String(left.createdAt)))[0] ||
    null;
}

// The in-memory record is only ever the one this hook was asked for: this
// executionId in this user's partition. Anything else is never reconciled,
// shown or edited (§9.8 user transitions, H8-2).
function belongsTo(record, executionId, userId) {
  return Boolean(record) && record.executionId === executionId && record.userId === userId;
}

function sameRecordFacts(left, right) {
  return left === right || (
    left.document === right.document &&
    left.timing === right.timing &&
    left.restTimer === right.restTimer &&
    left.restOffer === right.restOffer &&
    left.result === right.result &&
    left.leaseEpoch === right.leaseEpoch &&
    left.localRevision === right.localRevision &&
    left.ackedRevision === right.ackedRevision &&
    left.syncState === right.syncState &&
    left.lastSyncError === right.lastSyncError
  );
}

export default function useExecution(executionId, userId, {
  store = workoutLogStore,
  syncEngine = workoutSyncEngine,
  // True only while this screen holds the execution's editor lock (§9.4). A
  // non-writer mirrors IndexedDB and never writes, finalizes or dispatches.
  writer = true,
  // The athlete's Workout Preferences as currently known (cached or fetched;
  // null while unavailable, which resolves to the canonical defaults). Read
  // only at dispatch time: the mode and default are captured at anchor time
  // (§14.5) and never re-applied to an existing timer or offer.
  restPreferences = null,
  getHistoryApi = getWorkoutLogHistory,
} = {}) {
  const [record, setRecord] = useState(null);
  const recordRef = useRef(null);
  const [loading, setLoading] = useState(Boolean(executionId && userId));
  const [storageError, setStorageError] = useState(null);
  const [writable, setWritable] = useState(false);
  const [adoptedForEditing, setAdoptedForEditing] = useState(null);
  const writerRef = useRef(false);
  const writableRef = useRef(false);
  const handoffRef = useRef(null);
  const handoffGenerationRef = useRef(0);
  const handoffErrorRef = useRef(null);
  const coordinatorRef = useRef(null);
  const drainRef = useRef(null);
  const restPreferencesRef = useRef(restPreferences);
  restPreferencesRef.current = restPreferences;

  useEffect(() => {
    const coordinator = new ExecutionWriteCoordinator({
      store,
      onError: setStorageError,
    });
    coordinatorRef.current = coordinator;
    return () => {
      drainRef.current = coordinator.flush().catch(() => {});
      coordinator.dispose();
      coordinatorRef.current = null;
    };
  }, [store]);

  const adopt = useCallback((next) => {
    recordRef.current = next;
    setRecord(next);
    return next;
  }, []);

  const invalidate = useCallback((error) => {
    coordinatorRef.current?.invalidateExecution(executionId, error);
    recordRef.current = null;
    writableRef.current = false;
    setWritable(false);
    setRecord(null);
    setStorageError(error);
    setLoading(false);
  }, [executionId]);

  const handleWriteError = useCallback((error) => {
    if (error?.quarantined) invalidate(error);
    else setStorageError(error);
  }, [invalidate]);

  // The store answers every put with the canonical reconciled record. Memory
  // converges on it: a rejected stale candidate is replaced by the durable
  // truth (for example a terminal SAVED/DISCARDED record) and stops being
  // displayed or edited.
  const adoptPersisted = useCallback((persisted) => {
    const current = recordRef.current;
    if (!persisted || !current || current.executionId !== persisted.executionId) {
      return persisted || current;
    }
    const next = reconcileExecutionRecord(current, persisted);
    return sameRecordFacts(next, current) ? current : adopt(next);
  }, [adopt]);

  const reload = useCallback(async () => {
    if (!executionId || !userId) return null;
    const stored = await store.getExecution(userId, executionId);
    const current = belongsTo(recordRef.current, executionId, userId) ? recordRef.current : null;
    if (!stored && !current) {
      const quarantined = await confirmedQuarantineFor(store, userId, executionId);
      if (quarantined) throw quarantinedExecutionError(quarantined.reason);
    }
    const next = reconcileExecutionRecord(current, stored);
    adopt(next);
    setLoading(false);

    const sameExecution = current && stored &&
      current.executionId === stored.executionId && current.userId === stored.userId;
    // Only the lock holder converges IndexedDB on a reconciled record that the
    // store does not hold yet; a read-only tab never writes.
    if (writerRef.current && next && sameExecution && next !== stored) {
      const coordinator = coordinatorRef.current;
      void (coordinator?.commit(next) || store.putExecution(next))
        .then(adoptPersisted, handleWriteError);
    }
    return next;
  }, [adopt, adoptPersisted, executionId, handleWriteError, store, userId]);

  const persist = useCallback(async (next, { flush = false, syncReason = null } = {}) => {
    adopt(next);
    setStorageError(null);
    const coordinator = coordinatorRef.current;
    const settled = (coordinator?.commit(next) || store.putExecution(next)).then(
      (persisted) => ({ persisted, canonical: adoptPersisted(persisted) }),
      (error) => {
        handleWriteError(error);
        throw error;
      }
    );
    if (!flush) {
      void settled.catch(() => {});
      if (syncReason) syncEngine.trigger(syncReason, { userId: next.userId });
      return next;
    }
    if (coordinator) await coordinator.flush().catch(() => {});
    const { persisted, canonical } = await settled;
    // A write the store rejected in favour of a durable record announces
    // nothing: this tab's candidate was never stored.
    const accepted = Boolean(persisted) &&
      Number(persisted.localRevision) >= Number(next.localRevision) &&
      executionStatus(persisted) === executionStatus(next);
    if (syncReason && accepted) syncEngine.trigger(syncReason, { userId: next.userId });
    return canonical;
  }, [adopt, adoptPersisted, handleWriteError, store, syncEngine]);

  const finalizeDue = useCallback(async (now = new Date()) => {
    const current = recordRef.current;
    if (!current || !writerRef.current) return current;
    const action = finalizationAction(current, now);
    if (!action) return current;
    return persist(executionReducer(current, action), {
      flush: true,
      syncReason: "lifecycle",
    });
  }, [persist]);

  const dispatch = useCallback(async (action, {
    flush = false,
    syncReason = null,
  } = {}) => {
    if (!writerRef.current) throw executionOpenElsewhereError();
    await handoffRef.current;
    if (handoffErrorRef.current) throw handoffErrorRef.current;
    if (!writableRef.current) throw notEditableError();
    await finalizeDue();
    // Reduce from the canonical in-memory record as it is now, never from a
    // snapshot taken before an await: commits made in the same task apply in
    // order instead of overwriting each other.
    const current = recordRef.current;
    if (!current || !OPEN_STATUSES.has(executionStatus(current))) {
      throw notEditableError();
    }
    const reduced = executionReducer(current, action);
    // A semantic no-op returns the same record: nothing to persist or sync.
    if (reduced === current) return current;
    // §14.5: only this athlete transition may create rest state. Its timer or
    // offer is folded into the same record and revision, so the commit stays
    // one revision, one activity event, one write and one sync trigger.
    // Reload, sync adoption, handoff, mirroring and finalizeDue never get here.
    const next = applyRestAnchor(current, reduced, restPreferencesRef.current);
    return persist(next, { flush, syncReason });
  }, [finalizeDue, persist]);

  const flush = useCallback(
    () => coordinatorRef.current?.flush() || drainRef.current || Promise.resolve(),
    []
  );

  // §15.8 substitution / restore intent. Online only (the caller passes the
  // connectivity it knows; anything but a confirmed `true` is refused before
  // any local mutation). One replacement revision, persisted once, then one
  // immediate sync trigger; the reducer owns every other rule, including the
  // CONFIRM_DISCARD_REQUIRED guard for unperformed entered values.
  // LAST / Suggested inputs of an added or substituted item (§13.1). Fetched
  // online, stored on the local record without a revision, activity or sync,
  // and never awaited by the edit that caused it: any failure simply leaves
  // the item without guidance. Only the editor-lock holder writes.
  const refreshItemHistory = useCallback(async (itemId, { online = false } = {}) => {
    try {
      const current = recordRef.current;
      const item = current?.document?.items?.find((candidate) => candidate.itemId === itemId);
      const query = online === true && writerRef.current && needsOwnHistory(item)
        ? historyQueryForItem(current, item)
        : null;
      if (!query) return current || null;
      const entry = historyEntryFromResponse(await getHistoryApi(query), item);
      if (!entry) return recordRef.current;
      const updated = await store.setItemHistory(current.userId, current.executionId, itemId, entry);
      const latest = recordRef.current;
      if (!updated || !latest || latest.executionId !== current.executionId) return latest;
      return adopt({
        ...latest,
        server: { ...latest.server, history: withItemHistory(latest.server?.history, itemId, entry) },
      });
    } catch (_error) {
      return recordRef.current;
    }
  }, [adopt, getHistoryApi, store]);

  const substituteItem = useCallback(async (itemId, target, {
    online = false,
    confirmDiscard = false,
  } = {}) => {
    if (online !== true) {
      throw new SubstitutionError(
        SUBSTITUTION_ERRORS.OFFLINE,
        "Substituting an exercise needs a connection."
      );
    }
    const current = recordRef.current;
    if (!current) throw notEditableError();
    const slotKey = current.document?.items
      ?.find((item) => item.itemId === itemId)?.prescribedExerciseKey;
    const next = await dispatch(createSubstitutionAction(current, itemId, target, { confirmDiscard }), {
      flush: true,
      syncReason: "substitution",
    });
    // The replacement is a new item in the same slot (§15.8). The planned
    // exercise, chosen again, is PRESCRIBED again and uses its Start entry.
    const replacement = slotKey
      ? recordRef.current?.document?.items?.find((item) => item.prescribedExerciseKey === slotKey)
      : null;
    if (replacement) void refreshItemHistory(replacement.itemId, { online });
    return next;
  }, [dispatch, refreshItemHistory]);

  // Session-level picker Show all (§15.8): stored on the local record without
  // a revision, activity or sync. Only the editor-lock holder writes.
  const setPickerShowAll = useCallback(async (showAll) => {
    const current = recordRef.current;
    if (!current || !writerRef.current) return current;
    const updated = await store.setPickerShowAll(current.userId, current.executionId, showAll);
    const latest = recordRef.current;
    if (!updated || !latest || latest.executionId !== current.executionId) return latest;
    if ((latest.pickerShowAll === true) === (updated.pickerShowAll === true)) return latest;
    return adopt({ ...latest, pickerShowAll: updated.pickerShowAll === true });
  }, [adopt, store]);

  // A new user (or execution) never inherits the previous one's record: it is
  // dropped before this scope's handoff reads its own durable record.
  useEffect(() => {
    if (recordRef.current && !belongsTo(recordRef.current, executionId, userId)) {
      recordRef.current = null;
      setRecord(null);
      setLoading(Boolean(executionId && userId));
    }
  }, [executionId, userId]);

  // Ownership handoff. On every not-owner -> owner transition the latest
  // durable record is read and reconciled, due lazy finalization runs, and only
  // then does the screen become writable. A failed read leaves it read-only.
  useEffect(() => {
    handoffGenerationRef.current += 1;
    const generation = handoffGenerationRef.current;
    writerRef.current = Boolean(writer);
    writableRef.current = false;
    setWritable(false);
    handoffErrorRef.current = null;
    if (!executionId || !userId) {
      handoffRef.current = null;
      return undefined;
    }
    let cancelled = false;
    const fail = (error) => {
      if (cancelled) return;
      if (error?.quarantined) {
        invalidate(error);
        return;
      }
      setStorageError(error);
      setLoading(false);
    };
    if (!writer) {
      handoffRef.current = null;
      reload().catch(fail);
      return () => {
        cancelled = true;
      };
    }
    let markReady;
    let editingScheduled = false;
    handoffRef.current = new Promise((resolve) => {
      markReady = resolve;
    });
    (async () => {
      await reload();
      if (cancelled) return;
      await finalizeDue();
      if (cancelled || !recordRef.current) return;
      // Editing opens in a later commit than the one that shows the adopted
      // record, so no frame is editable while fields still display old values.
      editingScheduled = true;
      setAdoptedForEditing({ generation, ready: markReady });
    })().catch((error) => {
      if (!cancelled) handoffErrorRef.current = error;
      fail(error);
    }).finally(() => {
      if (!editingScheduled) markReady();
    });
    return () => {
      cancelled = true;
      writerRef.current = false;
      writableRef.current = false;
      markReady();
    };
  }, [executionId, finalizeDue, invalidate, reload, userId, writer]);

  useEffect(() => {
    if (!adoptedForEditing) return;
    if (
      adoptedForEditing.generation === handoffGenerationRef.current &&
      writerRef.current &&
      recordRef.current
    ) {
      writableRef.current = true;
      setWritable(true);
    }
    adoptedForEditing.ready();
    setAdoptedForEditing(null);
  }, [adoptedForEditing]);

  useEffect(() => {
    let active = true;
    const unsubscribe = syncEngine.subscribe((event) => {
      if (!active) return;
      // A push starting changes no stored record: nothing to reload.
      if (event?.type === "SYNC_ACTIVITY") return;
      if (
        event?.type === "EXECUTION_QUARANTINED" &&
        event.userId === userId &&
        event.executionId === executionId
      ) {
        invalidate(quarantinedExecutionError(event.code));
        return;
      }
      void reload().catch(setStorageError);
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [executionId, invalidate, reload, syncEngine, userId]);

  useEffect(() => {
    const run = () => {
      if (writableRef.current) void finalizeDue().catch(handleWriteError);
    };
    const interval = setInterval(run, 60000);
    const visible = () => {
      if (document.visibilityState === "visible") run();
    };
    document.addEventListener("visibilitychange", visible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [finalizeDue, handleWriteError]);

  // Until that effect runs, a record from another scope is withheld from render.
  const scoped = record === null || belongsTo(record, executionId, userId);

  return {
    record: scoped ? record : null,
    loading: loading || !scoped,
    storageError,
    writable,
    dispatch,
    flush,
    reload,
    finalizeDue,
    substituteItem,
    setPickerShowAll,
    refreshItemHistory,
  };
}

export { finalizationAction };
export { reconcileExecutionRecord };
